import type { FC } from "react";
import { useState, useEffect } from "react";

import { useQuery } from "@tanstack/react-query";

import type { IDynamicFilterRule } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import { chosenSites } from "app/common/services/chosenSites";
import { EAdsTabs } from "app/components/React/PlaResearch/type";
import { SWReactTableWrapperBox } from "app/components/React/Table/SWReactTableGhostLoader";
import {
    AD_TYPE,
    INCLUDE_EXCLUDE_KEYWORDS,
    INCLUDE_EXCLUDE_URL,
} from "app/components/widget/widget-filters/components/consts";
import type { IWidgetModelTypesType, IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import type { ISortProps } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import DurationServiceInstance from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";

import { CompareTable } from "./CompareTable";
import { SearchAdsTable } from "./search-ads-table/SearchAdsTable";
import {
    SHOPPING,
    TABLE_COLUMN_AD_DETAILS,
    TABLE_COLUMN_AD_TYPE,
    TABLE_COLUMN_AVG_CPC,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_CLICKS_SHARE,
    TABLE_COLUMN_KEYWORDS_COUNT,
    TABLE_COLUMN_LANDING_PAGE,
    TABLE_COLUMN_POSITION,
    TABLE_COLUMN_TOP_KEYWORD,
    TABLE_COLUMN_VOLUME,
    TEXT,
} from "app/common/keyword-research/website-search-ads/searchAdsTableConsts";
import type { IAdData, IDataRecord } from "app/common/keyword-research/website-search-ads/searchAdsTableTypes";
import { transformData } from "./search-ads-table/SearchAdsUtils";
import { useSearchAdsEndpoints } from "./searchAdsEndpoints";
import { adaptMultiIncludeExcludeRules } from "./searchAdsWebsiteUtils";

export const initialTableSort: ISortProps = { asc: false, sort: TABLE_COLUMN_CLICKS_SHARE };

const VISIBLE_COLUMNS = [
    TABLE_COLUMN_AD_TYPE,
    TABLE_COLUMN_AD_DETAILS,
    TABLE_COLUMN_CLICKS_SHARE,
    TABLE_COLUMN_CLICKS_CHANGE,
    TABLE_COLUMN_VOLUME,
    TABLE_COLUMN_AVG_CPC,
    TABLE_COLUMN_KEYWORDS_COUNT,
    TABLE_COLUMN_TOP_KEYWORD,
    TABLE_COLUMN_LANDING_PAGE,
    TABLE_COLUMN_POSITION,
];

interface ISearchAdsWebsiteTableProps {
    withAddToDashboard?: boolean;
    getColumnsFunction?: (
        visibleColumns: string[],
        currentPage: number,
        pageSize: number,
        sortConfig?: ISortProps,
        toggleExpandRow?: (key: number) => void,
        total?: number,
    ) => any;
}

export const SearchAdsWebsiteTable: FC<ISearchAdsWebsiteTableProps> = ({
    withAddToDashboard = true,
    getColumnsFunction,
}) => {
    const endpoints = useSearchAdsEndpoints();
    const {
        country,
        webSource,
        duration,
        key,
        selectedSite,
        selectedPageTab,
        pageFilter,
        multiIncludeExcludeKeywords,
        multiIncludeExcludeUrl,
    } = useSearchAdvertisingDataAndParams();
    const durationData = DurationServiceInstance.getDurationData(duration);
    const { from, to, isWindow } = durationData.forAPI;
    const isCompare = chosenSites.isCompare();
    const [currentPage, setCurrentPage] = useState(1);

    const getRequestBody = () => {
        try {
            const url: IDynamicFilterRule[] = multiIncludeExcludeUrl ? JSON.parse(multiIncludeExcludeUrl) : [];
            const keywords: IDynamicFilterRule[] = multiIncludeExcludeKeywords
                ? JSON.parse(multiIncludeExcludeKeywords)
                : [];
            return [
                ...url.map((rule) => ({ ...rule, field: "url" })),
                ...keywords.map((rule) => ({ ...rule, field: "keyword" })),
            ];
        } catch (e) {
            Sentry.error("Error making the post request:", e);
            return [];
        }
    };

    const requestBody = getRequestBody();
    const updateCurrentPage = (page: number) => {
        setCurrentPage(page);
    };

    const [sort, setSort] = useState<ISortProps>({
        sort: initialTableSort.sort,
        asc: initialTableSort.asc,
    });

    const updateSort = (sorter: ISortProps) => {
        setSort(sorter);
    };
    const getSelectedSite = (site: string) => site ?? key?.split(",")[0];

    const apiParams = {
        country,
        webSource,
        to,
        from,
        isWindow,
        sort: sort.sort,
        asc: sort.asc,
        keys: isCompare ? getSelectedSite(selectedSite) : key,
        adType: selectedPageTab === EAdsTabs[EAdsTabs.Text] ? TEXT : SHOPPING,
        pageFilterJson: pageFilter,
        page: currentPage,
        multiIncludeExcludeKeywords,
        multiIncludeExcludeUrl,
    };
    const paramsAsString = requestParams(apiParams);
    const excelUrl = `${endpoints.excel}?${paramsAsString}`;

    const { data, isLoading, isError } = useQuery({
        queryKey: [
            endpoints.table,
            to,
            from,
            duration,
            webSource,
            isWindow,
            key,
            country,
            selectedPageTab,
            getSelectedSite(selectedSite),
            sort.asc,
            sort.sort,
            pageFilter,
            currentPage,
            multiIncludeExcludeKeywords,
            multiIncludeExcludeUrl,
        ],
        queryFn: async () =>
            DefaultFetchService.getInstance().post(`${endpoints.table}?${paramsAsString}`, requestBody),
        select: (response: IDataRecord) => transformData(response),
    });

    const [adaptedKeywordsFilter, setAdaptedKeywordsFilter] = useState<string | undefined>();
    const [adaptedUrlFilter, setAdaptedUrlFilter] = useState<string | undefined>();

    useEffect(() => {
        const loadFilters = async () => {
            const keywordsFilter = multiIncludeExcludeKeywords
                ? await adaptMultiIncludeExcludeRules(multiIncludeExcludeKeywords)
                : undefined;
            const urlFilter = multiIncludeExcludeUrl
                ? await adaptMultiIncludeExcludeRules(multiIncludeExcludeUrl)
                : undefined;

            setAdaptedKeywordsFilter(keywordsFilter);
            setAdaptedUrlFilter(urlFilter);
        };

        void loadFilters();
    }, [multiIncludeExcludeKeywords, multiIncludeExcludeUrl]);

    const addToDashboardConfig = {
        type: "SearchAdsTable" as IWidgetModelTypesType,
        metric: "SearchAdsWebsite",
        modelType: "fromWebsite",
        country,
        duration,
        webSource,
        filters: {
            [AD_TYPE]: selectedPageTab === TEXT ? TEXT : SHOPPING.toLowerCase(),
            [INCLUDE_EXCLUDE_KEYWORDS]: adaptedKeywordsFilter,
            [INCLUDE_EXCLUDE_URL]: adaptedUrlFilter,
        },
    };

    const visibleColumns = VISIBLE_COLUMNS.filter((column) =>
        country === 999 ? column !== TABLE_COLUMN_POSITION : column,
    );

    const headerTooltipKey = "search.ads.website.table.header.tooltip";

    return (
        <SWReactTableWrapperBox data-automation="SearchAdsWebsiteTable">
            {isCompare ? (
                <CompareTable
                    data={data?.data}
                    isTableLoading={isLoading}
                    visibleColumns={visibleColumns}
                    isTableError={isError}
                    sortConfig={sort}
                    handleSort={updateSort}
                    totalCount={data?.totalCount}
                    currentPage={currentPage}
                    onPageChange={updateCurrentPage}
                    addToDashboardConfig={{
                        ...addToDashboardConfig,
                        country: country.toString(),
                        webSource: webSource as IWidgetModelTypesWebSource,
                    }}
                    headerTooltipKey={headerTooltipKey}
                    excelUrl={excelUrl}
                    requestBodyForExcel={requestBody}
                    getColumnsFunction={getColumnsFunction}
                />
            ) : (
                <SearchAdsTable<IAdData>
                    data={data?.data}
                    isLoading={isLoading}
                    visibleColumns={visibleColumns}
                    isError={isError}
                    sortConfig={sort}
                    handleSort={updateSort}
                    totalCount={data?.totalCount}
                    currentPage={currentPage}
                    onPageChange={updateCurrentPage}
                    addToDashboardConfig={
                        withAddToDashboard
                            ? {
                                  ...addToDashboardConfig,
                                  country: country.toString(),
                                  webSource: webSource as IWidgetModelTypesWebSource,
                              }
                            : undefined
                    }
                    headerTooltipKey={headerTooltipKey}
                    excelUrl={excelUrl}
                    requestBodyForExcel={requestBody}
                    getColumnsFunction={getColumnsFunction}
                />
            )}
        </SWReactTableWrapperBox>
    );
};
